import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useIsMobile } from "../lib/useMediaQuery";
import { sheetBackdropStyle, sheetPanelStyle } from "./sheet-style";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

const headerStyle: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--surface-overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--space-3)",
  padding: "var(--space-4) var(--space-5)",
  borderBottom: "1px solid var(--border-subtle)",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "var(--space-5)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-4)",
};

const footerStyle: CSSProperties = {
  position: "sticky",
  bottom: 0,
  background: "var(--surface-overlay)",
  display: "flex",
  justifyContent: "flex-end",
  gap: "var(--space-2)",
  padding: "var(--space-4) var(--space-5)",
  borderTop: "1px solid var(--border-subtle)",
};

const closeBtnStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 18,
  lineHeight: 1,
  minWidth: "var(--tap-min)",
  minHeight: "var(--tap-min)",
  display: "grid",
  placeItems: "center",
  borderRadius: "var(--radius-input)",
};

/**
 * Modal wrapper: centered Boardroom dialog on desktop, full-screen sheet on
 * mobile (sticky header + sticky footer so actions stay reachable while the
 * body scrolls). Backdrop click and Escape both close. Renders null when closed.
 * Routes existing modal bodies verbatim via {children}.
 */
export function Sheet({ open, onClose, title, footer, children }: SheetProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Escape-to-close (only while open).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Body-scroll-lock while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
      style={sheetBackdropStyle}
    >
      <div role="dialog" aria-modal="true" style={sheetPanelStyle(isMobile)}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>
            ×
          </button>
        </div>
        <div style={bodyStyle}>{children}</div>
        {footer && <div style={footerStyle}>{footer}</div>}
      </div>
    </div>
  );
}
